import { useMutation } from '@apollo/client';
import { SpecialFieldKeys } from '@bybeam/platform-types';
import Loading from '@platform-ui-common/components/Loading/index.js';
import usePartnerPrograms from '@platform-ui-common/hooks/contexts/PartnerPrograms';
import useErrorIndicator from '@platform-ui-common/hooks/useErrorIndicator.js';
import CreateApplicationMutation from '@platform-ui/application/graphql/CreateApplication.graphql';
import UpdateApplicationMutation from '@platform-ui/application/graphql/UpdateApplication.graphql';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import SubmitApplicationMutation from '@platform-ui/common/graphql/mutations/SubmitApplication.graphql';
import { getApplicantSections } from '@platform-ui/utilities/application/config.js';
import { getApplicationConfiguration } from '@platform-ui/utilities/application/configs/index.js';
import { getQuestionUpdate } from '@platform-ui/utilities/application/question.js';
import { Flex, Heading } from '@radix-ui/themes';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * ProcessAndSubmit component handles automatic application processing through email verification.
 *
 * Expected query parameters:
 * - programId (required): The program ID to create application for
 * - action (required): Either 'approve' (sets first question to 'Yes') or 'reject' (sets to 'No')
 * - code (required): The invitation code to include in the application
 */

export default function ProcessAndSubmit(): JSX.Element {
  const { user } = useAuth();
  const { programs } = usePartnerPrograms();
  const history = useHistory();

  const [createApplication] = useMutation(CreateApplicationMutation);
  const [updateApplication] = useMutation(UpdateApplicationMutation);
  const [submitApplication] = useMutation(SubmitApplicationMutation);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const showError = useErrorIndicator();

  const [didRun, setDidRun] = useState(false);

  useEffect(() => {
    // wait for auth + partner programs to be available
    if (!user?.core) return;
    if (!Array.isArray(programs) || programs.length === 0) return;

    // avoid double-run (React 18 StrictMode/dev double invoke)
    if (didRun) return;
    setDidRun(true);

    const run = async (): Promise<void> => {
      try {
        const params = new URLSearchParams(window.location.search);
        const programId = params.get('programId');
        const action = params.get('action');
        const linkCode = params.get('code');

        // programId is required for this flow
        if (!programId) {
          setErrorMessage('Missing required parameter: programId');
          showError('Missing required parameter: programId');
          return;
        }

        const program = programs.find(({ id }) => id === programId);

        // Use program from context for creating application.
        if (!program) {
          setErrorMessage('Program not found.');
          showError('Program not found.');
          return;
        }

        // action param is required for this flow and must be 'approve' or 'reject'
        if (!action || (action !== 'approve' && action !== 'reject')) {
          setErrorMessage(
            'Missing or invalid required parameter: action (must be "approve" or "reject")',
          );
          showError(
            'Missing or invalid required parameter: action (must be "approve" or "reject")',
          );
          return;
        }

        // linkCode is required for this flow
        if (!linkCode) {
          setErrorMessage('Missing required parameter: code');
          showError('Missing required parameter: code');
          return;
        }

        // Create application
        const {
          data: {
            application: {
              create: { metadata: createMeta, record },
            },
          },
        } = await createApplication({ variables: { input: { programId: program.id } } });

        if (!createMeta || createMeta.status !== 201) {
          setErrorMessage('Could not create application.');
          showError('Could not create application.');
          return;
        }

        const applicationId = createMeta.id;

        // Build an update containing a single answer using the first question in the config
        const config = getApplicationConfiguration(program, user.core);
        const sections = getApplicantSections(config);
        const firstQuestion = sections?.[0]?.questionGroups?.[0]?.questions?.[0];

        if (!firstQuestion) {
          // unable to determine question to answer
          setErrorMessage('Application configuration invalid: no question found.');
          showError('Application configuration invalid: no question found.');
          return;
        }

        const firstField = firstQuestion.fields?.[0];
        if (!firstField) {
          setErrorMessage('Application configuration invalid: no field found.');
          showError('Application configuration invalid: no field found.');
          return;
        }
        // Build the simple form data expected by getQuestionUpdate: map field.key to Yes/No based on action
        const answerValue = action === 'approve' ? 'Yes' : 'No';
        const formData: Record<string, unknown> = { [firstField.key]: answerValue };
        let content = getQuestionUpdate(firstQuestion, {}, formData);

        // If a link code was provided, include it under the special key defined in SpecialFieldKeys
        if (linkCode) {
          // Treat existing answers as a generic map so we can add dynamic keys like LinkCode
          const existingAnswers: Record<string, unknown> = (content.answers ?? {}) as Record<
            string,
            unknown
          >;
          const newAnswers: Record<string, unknown> = {
            ...existingAnswers,
            [SpecialFieldKeys.LinkCode]: linkCode,
          };
          // Cast back to the original answers shape without using `any`
          content = {
            ...content,
            answers: newAnswers as unknown as typeof content.answers,
          };
        }

        // Update application with the single answer
        const {
          data: {
            application: {
              update: { metadata: updateMeta } = {},
            } = {},
          } = {},
        } = await updateApplication({ variables: { input: { id: applicationId, content } } });

        if (!updateMeta || updateMeta.status >= 400) {
          setErrorMessage('Could not update application with provided answer.');
          showError('Could not update application with provided answer.');
          return;
        }

        // Submit the application
        const {
          data: {
            application: {
              submit: { metadata: submitMeta, record: updatedRecord } = {},
            } = {},
          } = {},
        } = await submitApplication({ variables: { input: { id: applicationId } } });

        if (!submitMeta || submitMeta.status >= 400) {
          setErrorMessage('Could not submit application.');
          showError('Could not submit application.');
          return;
        }

        // Success -> go to case details
        const caseId = updatedRecord?.case?.id;
        if (!caseId) {
          setErrorMessage('Could not determine case ID after submission.');
          showError('Could not determine case ID after submission.');
          return;
        }
        const caseRoute = ApplicantRoutes.CASE_DETAILS.replace(':caseId', caseId).concat(
          '?submitSuccess=true',
        );
        history.replace(caseRoute);
      } catch (err) {
        setErrorMessage('An unexpected error occurred.');
        showError('An unexpected error occurred.');
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, programs.length, history, didRun, setDidRun, showError]);

  // Keep the simple processing UI while the effect runs; show inline error if present
  if (errorMessage) {
    return (
      <Flex direction="column" align="center" justify="center" gap="6">
        <Heading size="8">{errorMessage}</Heading>
      </Flex>
    );
  }

  return (
    <Flex direction="column" align="center" justify="center" gap="6">
      <Loading size="XXL" fullPage />
      <Heading size="8">Processing your application</Heading>
    </Flex>
  );
}
